import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { effect, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthMode } from '@common/components/login/enums';
import { BrowserStorage, Provider, RoutePath, ServiceType } from '@common/enums';
import { AuthUser, Claim, OdataObject, ServiceFields, SystemInfo, UserServiceBackupInfo } from '@common/models';
import {
  AlternativeSignInPayload,
  AuthCallbackResult,
  AuthenticationPayload,
  AuthResponse,
  AuthUrlOData,
  PublicHelpSettings
} from '@common/models/auth/auth.model';
import { ODataServiceFactory } from '@common/odata';
import { SessionStorageService } from '@common/services';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { AuthSessionService } from '@common/services/auth/auth-session.service';
import { AuthStateService } from '@common/services/auth/auth-state.service';
import { FailedTaskPollingService } from '@common/services/failed-task-polling.service';
import { LocalStorageService } from '@common/services/storage/local-storage.service';
import { PersistentStateService } from '@common/services/storage/persistent-state.service';
import { getLoginErrorText, getPrefix, isAlternateSignIn } from '@common/utils';
import { WINDOW } from '@common/window.provider';
import { I18NextPipe } from 'angular-i18next';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly authContext = inject(AuthContextService);
  private readonly authSession = inject(AuthSessionService);
  private readonly authState = inject(AuthStateService);
  private readonly failedTaskPollingService = inject(FailedTaskPollingService);
  private readonly http = inject(HttpClient);
  private readonly jwtService = inject(JwtHelperService);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly odataFactory = inject(ODataServiceFactory);
  private readonly persistentService = inject(PersistentStateService);
  private readonly router = inject(Router);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly storageService = inject(LocalStorageService);
  private readonly window = inject(WINDOW);

  readonly #claimOdata$ = this.odataFactory.CreateService<Claim>('GetClaims');
  readonly #meOdata$ = this.odataFactory.CreateService<AuthUser>('me');
  readonly #odata$ = this.odataFactory.CreateService<any>('');

  protected readonly sessionEffect = effect(() =>
    this.sessionStorageService.set(BrowserStorage.ErrorPage, { model: this.authState.modelBasel() })
  );

  checkTokenInLocalStorage(): void {
    const token = this.storageService.get<string>(BrowserStorage.Token);

    try {
      if (token && Boolean(this.jwtService.decodeToken(token)) && !this.jwtService.isTokenExpired(token)) {
        this.authSession.setAuthenticated(true);
      }
    } catch (e) {
      this.authSession.setAuthenticated(false);
    }
  }

  // new endpoints
  login(provider: Provider, mode = AuthMode.None): Observable<AuthUrlOData> {
    const odataService = this.odataFactory.CreateService<AuthUrlOData>(`/Auth?provider=${provider}&mode=${mode}`);

    return this.http.get<AuthUrlOData>(odataService.Query().GetUrl());
  }

  getHost(sessionId: string): Observable<OdataObject<string>> {
    const odataService = this.odataFactory.CreateService<string>(`/Auth/GetHost?sessionId=${sessionId}`);

    return this.http.get<OdataObject<string>>(odataService.Query().GetUrl());
  }

  authCallback(payload: AuthenticationPayload): Observable<AuthCallbackResult> {
    const odataService = this.odataFactory.CreateService<AuthCallbackResult>(`/Auth`);

    return this.http.post<AuthResponse>(odataService.Query().GetUrl(), payload);
  }

  alternativeSignIn(payload: AlternativeSignInPayload): Observable<AuthResponse> {
    return this.alternativeAuthPost('/Auth/AlternativeSignIn', payload);
  }

  getToken(payload: AlternativeSignInPayload): Observable<AuthResponse> {
    return this.alternativeAuthPost('/Auth/GetToken', payload);
  }

  private alternativeAuthPost(url: string, payload: AlternativeSignInPayload): Observable<AuthResponse> {
    const odataService = this.odataFactory.CreateService<AuthResponse>(url);

    return this.http.post<AuthResponse>(odataService.Query().GetUrl(), payload);
  }

  getPath(res: AuthResponse): string {
    try {
      return getPrefix(res.Provider, isAlternateSignIn(res.Mode));
    } catch {
      throw new Error(`Caught error manually: "${res.Provider}" provider does not exist`);
    }
  }

  logout(res?: HttpErrorResponse): void {
    this.failedTaskPollingService.stopPolling();
    this.persistentService.data[BrowserStorage.SPointDisabled] = null;
    this.persistentService.data[BrowserStorage.SPointNoSiteBackup] = null;
    this.storageService.remove(BrowserStorage.Token);
    this.sessionStorageService?.remove(BrowserStorage.ErrorPage);
    this.authState.resetCache();
    this.authSession.setAuthenticated(false);
    this.authSession.setLogoutReason(getLoginErrorText(res, this.i18nPipe));

    void this.router.navigate([RoutePath.Login], { queryParams: null });
  }

  getSystemInfo(): Observable<SystemInfo> {
    return this.#odata$.CustomCollectionFunction('SystemInfo', {}).pipe(map((res) => res?.body));
  }

  getClaims(): Observable<Claim[]> {
    return this.#claimOdata$.Query().Exec();
  }

  getAvailableServices(): Observable<ServiceType[]> {
    return this.odataFactory
      .CreateService<string>('AvailableServices')
      .Query()
      .Exec()
      .pipe(
        map((res) => res.map((str) => ServiceType[str] as ServiceType)),
        map((services) => services.filter((s) => s !== ServiceType.History)) // `History` nav-link move to `Reporting`
      );
  }

  getAuthUser(): Observable<AuthUser> {
    return this.#meOdata$
      .Query()
      .Select([
        'Email',
        'DomainId',
        'DomainName',
        'Id',
        'InBackup',
        'Name',
        'Provider',
        'RestoreEnable',
        'SignInEnable',
        'UserProviderState'
      ])
      .Exec()
      .pipe(map((users) => (Array.isArray(users) ? users[0] : users)));
  }

  getAuthUserServiceInfo(serviceType: ServiceType): Observable<UserServiceBackupInfo> {
    const fields = new ServiceFields(serviceType);

    return this.#meOdata$
      .Query()
      .Select(fields.ServiceTypeStr)
      .Exec()
      .pipe(
        map((users) => (Array.isArray(users) ? users[0] : users)),
        map((user) => {
          return {
            LastBackupDate: user[fields.ServiceTypeStr].LastBackupInfo?.Date,
            TotalSize: user[fields.ServiceTypeStr].Size || 0
          };
        })
      );
  }

  signInAsAdmin(provider?: Provider): void {
    this.login(provider || this.authContext.provider(), AuthMode.SecondSignIn)
      .pipe(catchError((err: HttpErrorResponse) => (err instanceof HttpErrorResponse ? throwError(() => err) : EMPTY)))
      .subscribe({
        next: (data) => this.window.open(data.RedirectUrl, '_top'),
        error: (res: HttpErrorResponse) => this.logout(res)
      });
  }

  activateAltEmail(token: string): Observable<void> {
    return this.http.get<void>(`api/ActivateEmail?token=${token}`);
  }

  performAnotherAccountLogin(): void {
    this.login(this.authContext.provider(), AuthMode.AnotherAccount)
      .pipe(catchError((err: HttpErrorResponse) => (err instanceof HttpErrorResponse ? throwError(() => err) : EMPTY)))
      .subscribe({
        next: (data) => this.window.open(data.RedirectUrl, '_top'),
        error: (res: HttpErrorResponse) => this.logout(res)
      });
  }

  getPublicHelpSettings(): Observable<PublicHelpSettings> {
    const odataService = this.odataFactory.CreateService<PublicHelpSettings>('LoginPageSettings');

    return this.http.get<PublicHelpSettings>(odataService.Query().GetUrl());
  }
}
